import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import StudentForm from './StudentForm';
import StudentList from './StudentList';
import ParentManagement from './ParentManagement';
import SubjectManagement from './SubjectManagement';
import UserManagement from './UserManagement';
import UserForm from './UserForm';
import TutorManagement from './TutorManagement';
import ReviewsManagement from './ReviewsManagement';
import ReportManagement from './ReportManagement';
import CommunicationLog from './CommunicationLog';
import SettingsModal from './SettingsModal';
import SmtpSettingsModal from './SmtpSettingsModal';
import SmsSettingsModal from './SmsSettingsModal';
import SmsLogsModal from './SmsLogsModal';
import StripeSettingsModal from './StripeSettingsModal';
import PayUSettingsModal from './PayUSettingsModal';
import TransactionHistory from './TransactionHistory';
import AutomationSettings from './AutomationSettings';
import TemplateManager from './TemplateManager';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLogOut, FiPlus, FiUsers, FiUser, FiBookOpen, FiUserPlus, FiShield, FiSettings, FiMail, FiCpu, FiMessageSquare, FiCreditCard, FiBarChart, FiBriefcase, FiStar, FiReceipt, FiLayout } = FiIcons;

const Dashboard = () => {
  const { user, logout, hasPermission } = useAuth();
  const { appSettings, dataLoaded } = useData();
  const [activeTab, setActiveTab] = useState('students');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSmtpSettings, setShowSmtpSettings] = useState(false);
  const [showSmsSettings, setShowSmsSettings] = useState(false);
  const [showSmsLogs, setShowSmsLogs] = useState(false);
  const [showStripeSettings, setShowStripeSettings] = useState(false);
  const [showPayUSettings, setShowPayUSettings] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  const allTabs = [
    { id: 'students', label: 'Students', icon: FiUsers, permission: 'canManageStudents' },
    { id: 'tutors', label: 'Tutors', icon: FiBriefcase, permission: 'canManageUsers' },
    { id: 'reviews', label: 'Reviews', icon: FiStar, permission: 'canManageUsers' },
    { id: 'parents', label: 'Parents', icon: FiUserPlus, permission: 'canManageParents' },
    { id: 'subjects', label: 'Subjects', icon: FiBookOpen, permission: 'canManageSubjects' },
    { id: 'reports', label: 'Reports', icon: FiBarChart, permission: 'canViewReports' },
    { id: 'comms', label: 'Comm Log', icon: FiMessageSquare, permission: 'canManageStudents' },
    { id: 'automations', label: 'Automations', icon: FiCpu, permission: 'canManageUsers' },
    { id: 'users', label: 'Users', icon: FiShield, permission: 'canManageUsers' },
    { id: 'transactions', label: 'Transactions', icon: FiReceipt, permission: 'canViewReports' }
  ];

  const tabs = allTabs.filter(tab => {
    if (user?.role === 'admin') return true;
    return hasPermission(tab.permission);
  });

  const visibleTabs = tabs.length > 0 ? tabs : [allTabs[0]];

  return (
    <div className="min-h-screen bg-gray-50/50">
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-30">
        <div className="w-full px-6 lg:px-10">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: appSettings.brandColor }}>
                <SafeIcon icon={FiUsers} className="text-2xl" />
              </div>
              <div>
                <h1 className="text-xl font-black text-gray-900 tracking-tight">{appSettings.companyName}</h1>
                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">
                  {user?.role === 'admin' ? 'Administrator' : 'Management'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {user?.role === 'admin' && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setShowTemplateManager(true)} className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="Email Templates"><SafeIcon icon={FiLayout} /></button>
                  <button onClick={() => setShowPayUSettings(true)} className="p-3 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-all" title="PayU Settings"><SafeIcon icon={FiShield} /></button>
                  <button onClick={() => setShowStripeSettings(true)} className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="Stripe Settings"><SafeIcon icon={FiCreditCard} /></button>
                  <button onClick={() => setShowSmsLogs(true)} className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="SMS Logs"><SafeIcon icon={FiMessageSquare} /></button>
                  <button onClick={() => setShowSmtpSettings(true)} className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="SMTP Config"><SafeIcon icon={FiMail} /></button>
                  <button onClick={() => setShowSettings(true)} className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" title="App Settings"><SafeIcon icon={FiSettings} /></button>
                </div>
              )}
              <div className="h-8 w-px bg-gray-100 mx-2"></div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowProfileModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-xl font-bold transition-all border border-transparent hover:border-indigo-100">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-xs" />
                  </div>
                  Profile
                </button>
                <button onClick={logout} className="flex items-center gap-2 px-5 py-2.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl font-bold transition-all">
                  <SafeIcon icon={FiLogOut} /> Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-6 lg:px-10 py-10">
        <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 mb-10 p-2">
          <nav className="flex flex-wrap gap-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-6 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100 scale-105' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
              >
                <SafeIcon icon={tab.icon} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'students' && (
          <div className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h2 className="text-4xl font-black text-gray-900 tracking-tight">Students</h2>
                <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest">Enrollment & Academic Management</p>
              </div>
              {(user?.role === 'admin' || hasPermission('canManageStudents')) && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-indigo-200"
                  style={{ backgroundColor: appSettings.brandColor }}
                >
                  <SafeIcon icon={FiPlus} className="text-lg" />
                  Add New Student
                </motion.button>
              )}
            </div>
            <StudentList />
          </div>
        )}

        {activeTab === 'reports' && <ReportManagement />}
        {activeTab === 'tutors' && <TutorManagement />}
        {activeTab === 'reviews' && <ReviewsManagement />}
        {activeTab === 'parents' && <ParentManagement />}
        {activeTab === 'subjects' && <SubjectManagement />}
        {activeTab === 'automations' && <AutomationSettings />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'transactions' && <TransactionHistory />}
        {activeTab === 'comms' && <CommunicationLog />}

        {/* Modals */}
        {showAddForm && <StudentForm onClose={() => setShowAddForm(false)} onSave={() => setShowAddForm(false)} />}
        {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
        {showSmtpSettings && <SmtpSettingsModal onClose={() => setShowSmtpSettings(false)} />}
        {showSmsSettings && <SmsSettingsModal onClose={() => setShowSmsSettings(false)} />}
        {showSmsLogs && <SmsLogsModal onClose={() => setShowSmsLogs(false)} />}
        {showStripeSettings && <StripeSettingsModal onClose={() => setShowStripeSettings(false)} />}
        {showPayUSettings && <PayUSettingsModal onClose={() => setShowPayUSettings(false)} />}
        {showTemplateManager && <TemplateManager onClose={() => setShowTemplateManager(false)} />}
        {showProfileModal && (
          <UserForm user={user} onClose={() => setShowProfileModal(false)} onSave={() => setShowProfileModal(false)} />
        )}
      </main>
    </div>
  );
};

export default Dashboard;